/** Read-only escalation diagram for the advanced delivery rules.
 *
 *  This is the one genuinely branching thing in the composition model:
 *
 *      if [any|all] of ⟨trigger methods⟩ is ⟨Successful|Unsuccessful|Read|Unread⟩
 *      then after N minutes send ⟨dependent methods⟩
 *
 *  The editor for these lives in the notification editor's
 *  `AdvancedRulesSection` and is untouched by this page — here they are only
 *  drawn.
 *
 *  The load-bearing detail is `active`: rows are PRESERVED when the delivery
 *  ordering moves away from Advanced, so a diagram that just lists them would
 *  claim an escalation that never runs.
 */
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { Alert, Box, Chip, Paper, Stack, Typography } from '@mui/material';

import type { AnatomyAdvancedRule, AnatomyDeliveryMethod } from '@/api/notificationAnatomy';

function MethodChips({
  ids,
  methods,
  empty,
}: {
  ids: number[];
  methods: AnatomyDeliveryMethod[];
  empty: string;
}) {
  if (!ids.length) {
    return (
      <Typography variant="caption" color="warning.main">
        {empty}
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {ids.map((id) => {
        const dm = methods.find((m) => m.id === id);
        return (
          <Chip
            key={id}
            size="small"
            label={dm ? dm.method_label : `#${id}`}
            variant={dm && !dm.enabled ? 'outlined' : 'filled'}
            sx={{ height: 20, fontSize: '0.6875rem' }}
          />
        );
      })}
    </Stack>
  );
}

function Stage({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{ display: 'block', color: 'text.secondary', fontWeight: 600 }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function Arrow() {
  return <ArrowForwardIcon fontSize="small" sx={{ color: 'text.disabled', flexShrink: 0 }} />;
}

export function DeliveryRuleFlow({
  rule,
  methods,
  active,
}: {
  rule: AnatomyAdvancedRule;
  methods: AnatomyDeliveryMethod[];
  active: boolean;
}) {
  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, opacity: active ? 1 : 0.6, borderStyle: active ? 'solid' : 'dashed' }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start" flexWrap="wrap" useFlexGap>
        <Stage title={`If ${rule.condition_any_all_label.toUpperCase()} of`}>
          <MethodChips
            ids={rule.triggers}
            methods={methods}
            empty="no trigger methods — this rule can never fire"
          />
        </Stage>
        <Arrow />
        <Stage title="is">
          <Chip
            size="small"
            color={rule.condition_label === 'Unsuccessful' ? 'error' : 'default'}
            label={rule.condition_label}
            sx={{ height: 20, fontSize: '0.6875rem' }}
          />
        </Stage>
        <Arrow />
        <Stage title="then wait">
          <Stack direction="row" spacing={0.5} alignItems="center">
            <ScheduleIcon fontSize="small" sx={{ color: 'text.disabled' }} />
            <Typography variant="body2">
              {rule.send_after_min != null ? `${rule.send_after_min} min` : 'no delay'}
            </Typography>
          </Stack>
        </Stage>
        <Arrow />
        <Stage title="and send">
          <MethodChips
            ids={rule.dependents}
            methods={methods}
            empty="no dependent methods — nothing would be sent"
          />
        </Stage>
      </Stack>
      {!active && (
        <Alert severity="info" sx={{ mt: 1.5, py: 0 }}>
          Stored but inactive — the delivery-method ordering is not “Advanced”.
        </Alert>
      )}
    </Paper>
  );
}
